import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import {
  USER_STORAGE_KEY_NAME,
  USER_TOKEN_KEY_NAME,
} from "../constant/constats";
import { BehaviorSubject } from "rxjs";
@Injectable({
  providedIn: "root",
})
export class LocalStorageService {
  // ✅ SSR-safe: access to platformId so we never touch window/localStorage on the server
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    const initialUser = this.getLocalStorageData(USER_STORAGE_KEY_NAME);
    this.userSubject.next(initialUser);
  }

  // ✅ NEW: BehaviorSubject to track user state
  private userSubject = new BehaviorSubject<any>(null);
  user$ = this.userSubject.asObservable();

  /** SSR-safe check for browser environment */
  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /** SSR-safe accessor for window.localStorage */
  private get storage(): Storage | null {
    if (this.isBrowser && typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
    return null;
  }

  /** Static helper used from non-DI contexts (e.g. app.config.ts) */
  static getToken() {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage.getItem(USER_TOKEN_KEY_NAME);
    }
    return null;
  }

  getIsLogin() {
    return this.storage?.getItem(USER_TOKEN_KEY_NAME) ?? null;
  }

  getLocalStorageData(key: string) {
    const storage = this.storage;
    if (!storage) {
      return null;
    }

    const data = storage.getItem(key);
    if (!data) {
      return null;
    }

    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }

  setLocalStorageData(key: string, data: any) {
    const storage = this.storage;
    if (!storage) {
      return;
    }

    const value = typeof data === "string" ? data : JSON.stringify(data);
    storage.setItem(key, value);

    if (key === USER_STORAGE_KEY_NAME) {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      this.userSubject.next(parsedData);
    }
  }

  removeLocalStorageData(key: string) {
    const storage = this.storage;
    if (!storage) {
      return;
    }

    storage.removeItem(key);

    // ✅ Emit null if user is removed
    if (key === USER_STORAGE_KEY_NAME) {
      this.userSubject.next(null);
    }
  }

  clearLocalStorageData() {
    const storage = this.storage;
    if (!storage) {
      return;
    }

    storage.clear();
    this.userSubject.next(null); // ✅ clear user info
  }

  clearLoginDetail() {
    const storage = this.storage;
    if (!storage) {
      return;
    }

    storage.removeItem(USER_TOKEN_KEY_NAME);
    storage.removeItem(USER_STORAGE_KEY_NAME);
    this.userSubject.next(null); // ✅ clear user info
  }
}
